import { LangSwitcher } from 'lego-on-react';
import React, { useMemo } from 'react';

import { getI18nLang } from '@yandex-int/i18n';
import i18n from '@yandex-int/i18n';
import { NdaLink } from 'client/blocks/NdaLink/NdaLink';
import { moment } from 'client/lib/moment';
import { getIsYangoRequest } from 'client/utils';
import { YANDEX_COUNTRIES_LIST, YANGO_COUNTRIES_LIST } from 'client/utils/countries';
import * as keyset from 'i18n/Footer';
import * as regionsKeyset from 'i18n/Region';
import block from 'propmods';

import { routes } from '../../lib/routes';
import { Channel } from '../../model/skill';
import { getIsMultilingualSettingsEnabled, isIframe } from '../../utils';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import Caption from '../Form/Caption';
import Link from '../Link/Link';

import './Footer.scss';

const b = block('Footer');
const t = i18n(keyset);
const tRegions = i18n(regionsKeyset);

interface FooterProps {
    feedback?: string;
    channel?: Channel;
}

export default function Footer(props: FooterProps) {
    const { channel } = props;
    const currLang = getI18nLang();
    const lang = currLang[0].toUpperCase() + currLang.slice(1);
    if (isIframe()) {
        return null;
    }
    const isYangoRequest = getIsYangoRequest();
    const isMultilingualSettingsEnabled = getIsMultilingualSettingsEnabled();

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const disclaimer = useMemo(() => {
        if (!isMultilingualSettingsEnabled || channel !== Channel.SmartHome) return null;
        const currentLangKeyset = regionsKeyset[currLang as 'ru' | 'en'];
        const disclaimerCountriesList = getIsYangoRequest() ? YANDEX_COUNTRIES_LIST : YANGO_COUNTRIES_LIST;
        const countriesList = disclaimerCountriesList
            .map((code) => (code in currentLangKeyset ? tRegions(code) : code))
            .sort()
            .join(', ');
        const disclaimerText = isYangoRequest ? t('service-is-provided-by-Yandex') : t('service-is-provided-by-Yango');
        return (
            <div {...b('content')}>
                <Caption>{`* ${disclaimerText} ${countriesList}.`}</Caption>
            </div>
        );
    }, [isYangoRequest, currLang, isMultilingualSettingsEnabled, channel]);

    let docTo: string;
    let copyrightType: 'copyright' | 'copyrightYango';
    let feedback: string;
    if (isYangoRequest) {
        copyrightType = 'copyrightYango';
        feedback = routes.documentationYango.feedback;
        docTo = routes.documentationYango.index;
    } else {
        copyrightType = 'copyright';
        feedback = routes.documentation.aliceSkills.feedback;
        switch (channel) {
            case Channel.AliceSkill:
                docTo = routes.documentation.aliceSkills.about;
                break;
            case Channel.SmartHome:
                docTo = routes.documentation.smartHome.about;
                break;
            case Channel.Thereminvox:
                docTo = routes.documentation.thereminvox.index;
                break;
            default:
                docTo = routes.documentation.index;
                break;
        }
    }
    return (
        <ErrorBoundary>
            <footer {...b()}>
                <div {...b('content')}>
                    <div {...b('links')}>
                        <Link to={docTo} target="_blank">
                            {t('doc')}
                        </Link>
                        <Link to={feedback} target="_blank">
                            {t('feedback')}
                        </Link>
                    </div>
                    <div {...b('lang')}>
                        {/* LangSwitcher uses yandex.ru domain to perform language switch
                            todo: refactor (get rid of LangSwitcher)
                        */}
                        {!isYangoRequest && (
                            <div {...b('lang-switcher')}>
                                <LangSwitcher
                                    theme="normal"
                                    size="m"
                                    tld="ru"
                                    lang={lang.toLowerCase()}
                                    name={lang}
                                    retpath={location.href}
                                    nomore
                                    secretKey={__appConfig.secretkey}
                                >
                                    <LangSwitcher.Lang lang="ru" name="Ru" />
                                    <LangSwitcher.Lang lang="en" name="En" />
                                </LangSwitcher>
                            </div>
                        )}
                        <div {...b('copyright')}>
                            <NdaLink />
                            {t(copyrightType, { year: moment().year() })}
                        </div>
                    </div>
                </div>
                {disclaimer}
            </footer>
        </ErrorBoundary>
    );
}
